import {
  HStack,
  Skeleton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import React, { useMemo } from 'react';

import { Bank } from '@finley-technologies/finley-types';
import {
  FinleyLoanParty,
  ValleyBankCollateralAccount,
} from '@finley-technologies/valley-bank-utils';

import {
  CsvXlsxDownloadSelect,
  getTanstackVisibleDataForDownload,
} from '~/packages/common-frontend/components/CsvXlsxDownloadSelect';
import { ColumnDisplaySettingsButton } from '~/packages/common-frontend/components/TanStack/ColumnReordering';
import {
  getColumnNames,
  TableColumnConfigurationKey,
  createDefaultTableColumnConfiguration,
  useTableColumnPreferences,
} from '~/packages/common-frontend/hooks/useTableColumnPreferences';
import {
  formatCurrencyAmount,
  percentageToPercentageLabel,
} from '~/packages/common-frontend/utils';
import {
  CollateralAccountArrangementGroupNumbers,
  getArrangementGroupNumbers,
} from '~/valleyBank/domains/Collateral/ui/CollateralAccountArrangementGroupNumbers';
import { CollateralAccountLoanAccountNumbers } from '~/valleyBank/domains/Collateral/ui/CollateralAccountLoanAccountNumbers';
import { CustomerSummaryLink } from '~/valleyBank/domains/Customers/hooks/CustomerContext';
import { getCustomerFromReceivable } from '~/valleyBank/domains/Customers/utils';

const TABLE_KEY: TableColumnConfigurationKey =
  'valleyBankSbl.arrangementGroupCollateralAccounts';

const DEFAULT_COLUMN_CONFIG = createDefaultTableColumnConfiguration({
  tableKey: TABLE_KEY,
  columns: [
    {
      columnKey: 'accountNumber',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'productName',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'marketValue',
      isVisible: true,
      isFixed: false,
    },

    {
      columnKey: 'effectiveAdvanceRate',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'preAllocationLendableValue',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'collateralType',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'customer',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'loanAccountNumbers',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'arrangementGroupNumbers',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'cifNum',
      isVisible: true,
      isFixed: false,
    },
  ],
});

type CollateralAccountsTableProps = {
  arrangementGroupNumber: string;
  collateralAccounts: ValleyBankCollateralAccount[];
  isLoading: boolean;
  isError: boolean;
  loanParties: FinleyLoanParty[];
};

const generateColumnDefs = (
  loanParties: FinleyLoanParty[],
): ColumnDef<ValleyBankCollateralAccount, any>[] => [
  {
    id: 'accountNumber',
    accessorKey: 'accountNumber',
    header: 'Account Number',
  },
  {
    id: 'productName',
    accessorKey: 'productName',
    header: 'Product Name',
  },
  {
    id: 'loanAccountNumbers',
    header: 'Loan Accounts',
    accessorFn: (row) => {
      return (
        row.receivables[0]?.receivableReponseTapeRow.baseFields
          .finley_assigned_to_loan_accounts || 'Unassigned'
      );
    },
    cell: function formatLoanAccountNumbers({
      row: {
        original: { receivables },
      },
    }) {
      return <CollateralAccountLoanAccountNumbers receivables={receivables} />;
    },
  },
  {
    id: 'arrangementGroupNumbers',
    header: 'Arrangement Group',
    accessorFn: (row) => {
      return getArrangementGroupNumbers(row.receivables).join('|');
    },
    cell: function formatArrangementGroupNumbers({
      row: {
        original: { receivables },
      },
    }) {
      return (
        <CollateralAccountArrangementGroupNumbers receivables={receivables} />
      );
    },
  },
  {
    id: 'collateralType',
    header: 'Collateral Type',
    accessorFn: function formatCollateralType(row) {
      return row.receivables[0]?.receivableReponseTapeRow.baseFields
        .collateral_account_type;
    },
    cell: function formatCollateralType({ getValue }) {
      return getValue() || '-';
    },
  },
  {
    id: 'cifNum',
    accessorKey: 'cifNum',
    header: 'CIF Number',
    cell: function formatCifNum({ getValue }) {
      return (
        <CustomerSummaryLink cifnum={getValue()}>
          {getValue()}
        </CustomerSummaryLink>
      );
    },
  },
  {
    id: 'customer',
    header: 'Customer',
    accessorFn: (row) => {
      const customer = getCustomerFromReceivable(row.receivables, loanParties);

      return customer?.full_name || row.owner?.full_name || '-';
    },
    cell: function formatCustomer({
      getValue,
      row: {
        original: { cifNum },
      },
    }) {
      return (
        <CustomerSummaryLink cifnum={cifNum}>{getValue()}</CustomerSummaryLink>
      );
    },
  },
  {
    id: 'marketValue',
    accessorKey: 'marketValue',
    header: 'Market Value',
    cell: function formatMarketValue({ getValue }) {
      return formatCurrencyAmount(getValue(), Bank.Currency.USD);
    },
  },
  {
    id: 'effectiveAdvanceRate',
    accessorKey: 'effectiveAdvanceRate',
    header: 'Blended Advance Rate',
    cell: function formatEffectiveAdvanceRate({ getValue }) {
      return percentageToPercentageLabel(getValue() * 100, 2);
    },
  },
  {
    id: 'preAllocationLendableValue',
    accessorKey: 'preAllocationLendableValue',
    header: 'Lendable Value',
    cell: function formatPreAllocationLendableValue({ getValue }) {
      return formatCurrencyAmount(getValue(), Bank.Currency.USD);
    },
  },
];
export const CollateralAccountsTable = ({
  arrangementGroupNumber,
  collateralAccounts,
  isLoading,
  isError,
  loanParties,
}: CollateralAccountsTableProps) => {
  const {
    columnConfiguration,
    columnOrder,
    columnVisibility,
    updateUserColumnConfiguration,
  } = useTableColumnPreferences({
    tableKey: TABLE_KEY,
    defaultTableColumnConfiguration: DEFAULT_COLUMN_CONFIG,
  });

  const { columnDefs, columnNames } = useMemo(() => {
    const _columnDefs = generateColumnDefs(loanParties);
    return {
      columnDefs: _columnDefs,
      columnNames: getColumnNames(_columnDefs),
    };
  }, [loanParties]);

  const table = useReactTable({
    data: collateralAccounts,
    columns: columnDefs,
    state: {
      columnOrder,
      columnVisibility,
    },
    getCoreRowModel: getCoreRowModel(),
  });

  const csvDownload = getTanstackVisibleDataForDownload({
    tableInstance: table,
  });
  return (
    <>
      <HStack alignSelf="flex-end" spacing="1em">
        <ColumnDisplaySettingsButton
          initialColumns={columnConfiguration}
          updateColumnConfigs={updateUserColumnConfiguration}
          columnNames={columnNames}
        />
        <CsvXlsxDownloadSelect
          dataRows={csvDownload.dataRows}
          headers={csvDownload.headers}
          filenamePrefix={`collateral-accounts-arrangement-group-${arrangementGroupNumber}`}
        />
      </HStack>

      <TableContainer
        mt="32px"
        // maxH required for table top sticky header to work
        // since position:sticky attaches to the nearest ancestor with a scrolling mechanism
        // https://stackoverflow.com/questions/70770027/table-with-sticky-header-and-horizontal-scroll
        overflowX="auto"
        overflowY="auto"
        position="relative"
        id="receivables-dashboard-main-table"
        w="100%"
      >
        <Table id="valley-bank-arrangement-group-collateral-accounts-table">
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <Th key={header.id}>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </Th>
                  );
                })}
              </Tr>
            ))}
          </Thead>
          {isLoading && !isError && (
            <Tbody>
              {Array.from(Array(10).keys()).map((i) => (
                <Tr key={i}>
                  {table.getVisibleFlatColumns().map((column) => (
                    <Td
                      key={`${column.id}+${i}`}
                      padding="8px 16px"
                      height="56px"
                    >
                      <Skeleton
                        h="100%"
                        w={
                          column.columnDef.header === 'Borrower'
                            ? '400px'
                            : undefined
                        }
                      />
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          )}
          {!isLoading && !isError && (
            <Tbody>
              {table.getRowModel().rows.map((row) => (
                <Tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <Td
                      key={cell.id}
                      textAlign={cell.column.columnDef.meta?.style?.textAlign}
                      padding={cell.column.columnDef.meta?.style?.padding}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          )}
        </Table>
      </TableContainer>
    </>
  );
};
